import { observable, makeObservable, computed } from 'mobx'

import { Id } from 'types/definitions'
import { Model } from 'infra/model'
import { DateZero, maybeParseDate } from 'utils/date-util'

import { BusinessInfoDto, BusinessProfileVersionDto } from './dtos'
import { ContactInfo } from '../ContactInfo'
import { timestampToISO } from 'utils/grpc-util'
import { once } from 'lodash'

export class BusinessProfileVersion extends Model<
  BusinessProfileVersionDto,
  BusinessProfileVersion
> {
  @observable
  id = Id.none

  @observable
  version = 0

  @observable
  workspaceId = ''

  @observable
  ownerUserId = ''

  @observable
  businessInfo?: BusinessInfo

  @observable
  contactInfo?: ContactInfo

  @observable
  createTime = DateZero()

  @observable
  updateTime = DateZero()

  static get none() {
    return lazyNone()
  }

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<BusinessProfileVersionDto>,
  ): Partial<BusinessProfileVersion> {
    return {
      id: attrs.id,
      version: attrs.version,
      workspaceId: attrs.workspaceId,
      ownerUserId: attrs.ownerUserId,
      businessInfo: attrs.businessInfo
        ? new BusinessInfo().set(attrs.businessInfo, { parse: true })
        : attrs.businessInfo,
      contactInfo: attrs.contactInfo
        ? new ContactInfo().set(attrs.contactInfo, { parse: true })
        : attrs.contactInfo,
      createTime: maybeParseDate(timestampToISO(attrs.createTime)),
      updateTime: maybeParseDate(timestampToISO(attrs.createTime)),
    }
  }
}

export class BusinessInfo extends Model<BusinessInfoDto, BusinessInfo> {
  @observable
  id = Id.none

  @observable
  businessName = ''

  @observable
  dateOfIncorporation = ''

  @observable
  employerIdentificationNumber = ''

  @computed
  get employerIdentificationNumberDisplay() {
    return this.employerIdentificationNumber.replace(
      /\d{2}-.*(\d{3})$/,
      '✶✶-✶✶✶✶$1',
    )
  }

  constructor() {
    super()
    makeObservable(this)
  }

  parse(attrs: Partial<BusinessInfoDto>): Partial<BusinessInfo> {
    return {
      businessName: attrs.businessName,
      dateOfIncorporation: attrs.dateOfIncorporation,
      employerIdentificationNumber: attrs.employerIdentificationNumber,
    }
  }
}

const lazyNone = once(() => new BusinessProfileVersion())
